/**
 * Churn Telemetry · T7126 · "subscript thrash" detector.
 *
 * Fires `sdk.churn.subscript-thrash` to Better Stack when a hook's
 * acquire→release pair completes inside `VIOLATION_WINDOW_MS` (50 ms by
 * default). Sample rate is 1% (one in 100 violating pairs) and per-tuple
 * cooldown is 60 s, so a runaway hook can still spam at most 1 event/min
 * to the alert pipeline.
 *
 * The dev-mode assert that catches the SAME class of bug at coding time
 * is a separate task (T7128). This file is the production telemetry path:
 * sampled, rate-limited, side-channel only.
 */

/** Acquire→release pairs faster than this are "thrash". */
export const CHURN_VIOLATION_WINDOW_MS = 50;

/** Sampling rate applied AFTER the violation predicate (1%). */
export const CHURN_SAMPLE_RATE = 0.01;

/** Per-(specName, queryKeyHash) cooldown in ms — caps to one event/min. */
export const CHURN_COOLDOWN_MS = 60_000;

export interface ChurnTelemetry {
  /**
   * Called from the registry release path. If the acquire→release pair
   * dwell < `CHURN_VIOLATION_WINDOW_MS`, with probability `CHURN_SAMPLE_RATE`
   * and respecting the per-tuple cooldown, emits one telemetry event.
   */
  reportAcquireReleasePair(
    specName: string,
    queryKeyHash: string,
    acquiredAt: number,
    releasedAt: number,
  ): void;
}

/**
 * Factory. `emit` is wired to the Better Stack sink at SDK init. `now` is
 * injected so unit tests can drive the cooldown clock with vitest fake
 * timers; production passes `Date.now`.
 */
export function makeChurnTelemetry(
  emit: (event: 'sdk.churn.subscript-thrash', payload: object) => void,
  opts: {
    now?: () => number;
    rand?: () => number;
    sampleRate?: number;
    cooldownMs?: number;
    violationWindowMs?: number;
  } = {},
): ChurnTelemetry {
  const now = opts.now ?? Date.now;
  const rand = opts.rand ?? Math.random;
  const sampleRate = opts.sampleRate ?? CHURN_SAMPLE_RATE;
  const cooldownMs = opts.cooldownMs ?? CHURN_COOLDOWN_MS;
  const window = opts.violationWindowMs ?? CHURN_VIOLATION_WINDOW_MS;

  const lastEmit = new Map<string, number>();

  function reportAcquireReleasePair(
    specName: string,
    queryKeyHash: string,
    acquiredAt: number,
    releasedAt: number,
  ): void {
    const dwellMs = releasedAt - acquiredAt;
    if (dwellMs > window) return;
    if (rand() >= sampleRate) return;
    const key = `${specName}|${queryKeyHash}`;
    const prior = lastEmit.get(key);
    const t = now();
    // First-emit-per-tuple has no cooldown anchor; only subsequent emits
    // are gated. Without this branch the very first sampled hit at t=0
    // (the canonical cold-start case) would be suppressed.
    if (prior !== undefined && t - prior < cooldownMs) return;
    lastEmit.set(key, t);
    emit('sdk.churn.subscript-thrash', {
      specName,
      queryKeyHash,
      dwellMs,
    });
  }

  return { reportAcquireReleasePair };
}
